import React, { useState, useEffect, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepIconProps,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { supabaseData } from '../utils/supabase-data-client';
import { lookupAaguid } from '../utils/aaguidRegistry';

const API_BASE =
  'https://qaqj2uc5pc.execute-api.us-east-2.amazonaws.com/prod/api';

interface Invitation {
  id: string;
  email: string;
  invite_token: string;
  status: string;
  invited_by: string;
  created_at: string;
  approved_at: string | null;
  type: string | null;
  source_ip: string | null;
  geo_location: string | null;
  expires_at: string | null;
}

// Minutes a one-click device approval stays valid. Matches the window used by
// the manual "approve by IP" flow.
const DEVICE_APPROVAL_MINUTES = 30;

// Returns a short human string describing how much time is left before the
// timestamp, or an "expired" marker.
function timeRemaining(expiresAt: string | null): {
  expired: boolean;
  label: string;
} {
  if (!expiresAt) return { expired: false, label: '' };
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { expired: true, label: 'expired' };
  const mins = Math.round(ms / 60000);
  if (mins < 60) return { expired: false, label: `${mins} min left` };
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return { expired: false, label: `${hrs} hr left` };
  return { expired: false, label: `${Math.round(hrs / 24)} days left` };
}

interface Credential {
  id: string;
  username: string;
  credential_id: string;
  friendly_name: string | null;
  created_at: string;
  last_used_at: string | null;
  counter: number;
  backup_eligible: boolean | null;
  backed_up: boolean | null;
  aaguid: string | null;
}

interface UserGroup {
  username: string;
  credentials: Credential[];
  lastLogin: string | null;
}

// --- Invitation Stepper ---

const STEPS = ['Invited', 'Pending', 'Approved', 'Registered'];

function getActiveStep(status: string): number {
  switch (status) {
    case 'pending':
      return 1;
    case 'approved':
      return 2;
    case 'registered':
      return 3;
    case 'denied':
      return 1;
    default:
      return 0;
  }
}

function CustomStepIcon(
  props: StepIconProps & { status: string; stepIndex: number },
) {
  const { active, completed, status, stepIndex } = props;

  if (status === 'denied' && stepIndex === 1) {
    return <CancelIcon color="error" />;
  }
  if (status === 'registered' && stepIndex === 3) {
    return <CheckCircleIcon color="success" />;
  }
  if (completed) {
    return <CheckCircleIcon color="success" />;
  }
  if (active) {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 12,
          fontWeight: 'bold',
        }}
      >
        {stepIndex + 1}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        bgcolor: 'grey.300',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'grey.600',
        fontSize: 12,
      }}
    >
      {stepIndex + 1}
    </Box>
  );
}

// --- Invitations Tab ---

function InvitationsTab() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Manual device-approval dialog: admin supplies the IP the registering
  // device will use (register_begin matches source_ip exactly).
  const [deviceApproval, setDeviceApproval] = useState<{
    email: string;
    ip: string;
  } | null>(null);
  const [approvingDevice, setApprovingDevice] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabaseData
      .from('registration_invitations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load invitations',
        severity: 'error',
      });
    } else {
      setInvitations(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const sendInvite = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        message: 'Email is required',
        severity: 'error',
      });
      return;
    }
    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/registration-invite/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, invitedBy: 'admin' }),
      });
      const result = await response.json();
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Invite sent for approval',
          severity: 'success',
        });
        setEmail('');
        fetchInvitations();
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to send invite',
          severity: 'error',
        });
      }
    } catch {
      setSnackbar({ open: true, message: 'Network error', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Issue AND approve an invitation in one step, bypassing the email round-trip.
  // Inserts an approved type='invite' row directly (RLS is off on this table),
  // so the invited user can immediately "Set up a new account". Use when you're
  // with the user or don't need the email approval flow.
  const sendAndApproveInvite = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        message: 'Email is required',
        severity: 'error',
      });
      return;
    }
    setSending(true);
    const token =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, '')
        : Math.random().toString(36).slice(2);
    const { error } = await supabaseData
      .from('registration_invitations')
      .insert({
        email: email.toLowerCase(),
        type: 'invite',
        status: 'approved',
        invite_token: token,
        invited_by: 'admin',
        approved_at: new Date().toISOString(),
        // 7-day window to complete registration.
        expires_at: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      });
    setSending(false);
    if (error) {
      setSnackbar({
        open: true,
        message: `Failed to issue invite: ${error.message}`,
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: `${email.toLowerCase()} is approved (valid 7 days). They can set up a new account now.`,
        severity: 'success',
      });
      setEmail('');
      fetchInvitations();
    }
  };

  const approveInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    try {
      const response = await fetch(`${API_BASE}/registration-invite/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: invitation.invite_token }),
      });
      const result = await response.json();
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Invitation approved',
          severity: 'success',
        });
        fetchInvitations();
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to approve',
          severity: 'error',
        });
      }
    } catch {
      setSnackbar({ open: true, message: 'Network error', severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const denyInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    const { error } = await supabaseData
      .from('registration_invitations')
      .update({ status: 'denied' })
      .eq('id', invitation.id);

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to deny invitation',
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Invitation denied',
        severity: 'success',
      });
      fetchInvitations();
    }
    setActionLoading(null);
  };

  const resendInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    try {
      const response = await fetch(`${API_BASE}/registration-invite/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: invitation.email, invitedBy: 'admin' }),
      });
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Invite resent',
          severity: 'success',
        });
        fetchInvitations();
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to resend',
          severity: 'error',
        });
      }
    } catch {
      setSnackbar({ open: true, message: 'Network error', severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const revokeInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    const { error } = await supabaseData
      .from('registration_invitations')
      .update({ status: 'approved' })
      .eq('id', invitation.id);

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to revoke',
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Invitation revoked (set to approved)',
        severity: 'success',
      });
      fetchInvitations();
    }
    setActionLoading(null);
  };

  const reapproveInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    const { error } = await supabaseData
      .from('registration_invitations')
      .update({
        status: 'approved',
        expires_at: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      })
      .eq('id', invitation.id);

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to re-approve',
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Invitation re-approved (7 day expiry)',
        severity: 'success',
      });
      fetchInvitations();
    }
    setActionLoading(null);
  };

  // Manually create an approved device request for a given IP. This is the
  // admin override for when the email-based device-approval flow can't be used
  // (e.g. SES unavailable). register_begin looks for an approved type='device'
  // row whose source_ip matches the request and hasn't expired.
  const approveDeviceForIp = async (email: string, ip: string) => {
    setApprovingDevice(true);
    // Generate a token so the row is self-contained (matches the shape the
    // device-request lambda would have created).
    const token =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, '')
        : Math.random().toString(36).slice(2);
    const { error } = await supabaseData
      .from('registration_invitations')
      .insert({
        email: email.toLowerCase(),
        type: 'device',
        status: 'approved',
        invite_token: token,
        invited_by: 'admin',
        source_ip: ip.trim(),
        approved_at: new Date().toISOString(),
        // Generous window so the human has time to complete the passkey ceremony.
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      });

    setApprovingDevice(false);
    if (error) {
      setSnackbar({
        open: true,
        message: `Failed to approve device: ${error.message}`,
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: `Device approved for ${ip.trim()} (valid ${DEVICE_APPROVAL_MINUTES} min). Register on that device now.`,
        severity: 'success',
      });
      setDeviceApproval(null);
      fetchInvitations();
    }
  };

  // One-click approval for a pending device request. The pending row already
  // carries the correct source_ip (captured when the device asked), so we just
  // flip it to approved and set a generous expiry — no IP typing needed.
  const approvePendingDevice = async (request: Invitation) => {
    setActionLoading(request.id);
    const { error } = await supabaseData
      .from('registration_invitations')
      .update({
        status: 'approved',
        approved_at: new Date().toISOString(),
        expires_at: new Date(
          Date.now() + DEVICE_APPROVAL_MINUTES * 60 * 1000,
        ).toISOString(),
      })
      .eq('id', request.id);

    if (error) {
      setSnackbar({
        open: true,
        message: `Failed to approve: ${error.message}`,
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: `Approved for ${request.source_ip} (valid ${DEVICE_APPROVAL_MINUTES} min). On that device, tap "Use a new device" again to finish.`,
        severity: 'success',
      });
      fetchInvitations();
    }
    setActionLoading(null);
  };

  // Best-effort public IP detection for the manual approve-by-IP dialog.
  const autoDetectIp = async () => {
    try {
      const resp = await fetch('https://api.ipify.org?format=json');
      const data = await resp.json();
      if (data?.ip) {
        setDeviceApproval((prev) => (prev ? { ...prev, ip: data.ip } : prev));
      }
    } catch {
      setSnackbar({
        open: true,
        message: 'Could not auto-detect IP. Enter it manually.',
        severity: 'error',
      });
    }
  };

  const deleteInvite = async (invitation: Invitation) => {
    setActionLoading(invitation.id);
    const { error } = await supabaseData
      .from('registration_invitations')
      .delete()
      .eq('id', invitation.id);

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete',
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Invitation deleted',
        severity: 'success',
      });
      fetchInvitations();
    }
    setActionLoading(null);
  };

  const isExpired = (invitation: Invitation): boolean => {
    if (invitation.status !== 'approved' || !invitation.approved_at)
      return false;
    const approvedDate = new Date(invitation.approved_at);
    const now = new Date();
    const diffDays =
      (now.getTime() - approvedDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 7;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Split rows: pending device requests get their own prominent section; the
  // rest (invites, and resolved device rows) show in the invitation list.
  const pendingDeviceRequests = invitations.filter(
    (inv) => inv.type === 'device' && inv.status === 'pending',
  );
  const invitationRows = invitations.filter(
    (inv) => !(inv.type === 'device' && inv.status === 'pending'),
  );

  return (
    <Box>
      {/* How-to instructions (collapsible) */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Box
          onClick={() => setInstructionsOpen((prev) => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Adding a new device (e.g. a work computer)
          </Typography>
          <IconButton
            size="small"
            aria-label={
              instructionsOpen ? 'Hide instructions' : 'Show instructions'
            }
          >
            {instructionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={instructionsOpen}>
          <Typography
            variant="body2"
            color="text.secondary"
            component="ol"
            sx={{ pl: 2, m: 0, mt: 1 }}
          >
            <li>
              On the new device, open the app, enter your email, and choose{' '}
              <strong>&ldquo;Use a new device&rdquo;</strong>. It will say the
              device isn&apos;t approved and let you request access.
            </li>
            <li>
              That request appears below under{' '}
              <strong>Device Access Requests</strong> with the device&apos;s IP.
              Click <strong>Approve</strong> (valid {DEVICE_APPROVAL_MINUTES}{' '}
              min).
            </li>
            <li>
              Back on the new device, choose{' '}
              <strong>&ldquo;Use a new device&rdquo;</strong> again to finish
              creating the passkey.
            </li>
          </Typography>
        </Collapse>
      </Paper>

      {/* Pending device access requests */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">
            Device Access Requests
            {pendingDeviceRequests.length > 0 && (
              <Chip
                label={pendingDeviceRequests.length}
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <IconButton onClick={fetchInvitations} title="Refresh" size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
        {pendingDeviceRequests.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No pending device requests. When someone taps &ldquo;Use a new
            device&rdquo; and requests access, it shows up here for one-click
            approval.
          </Typography>
        ) : (
          pendingDeviceRequests.map((req) => (
            <Paper
              key={req.id}
              variant="outlined"
              sx={{ p: 2, mb: 1, bgcolor: 'background.paper' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {req.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    IP: <strong>{req.source_ip || 'unknown'}</strong>
                  </Typography>
                  {req.geo_location && (
                    <Typography variant="body2" color="text.secondary">
                      {req.geo_location}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Requested {new Date(req.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={actionLoading === req.id}
                    onClick={() => approvePendingDevice(req)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={actionLoading === req.id}
                    onClick={() => denyInvite(req)}
                  >
                    Deny
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    disabled={actionLoading === req.id}
                    onClick={() => deleteInvite(req)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Paper>

      {/* Send new invite form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send New Invitation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Email to Invite"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <Button
            onClick={sendInvite}
            disabled={sending}
            variant="outlined"
            size="medium"
          >
            {sending ? 'Sending...' : 'Send Invite'}
          </Button>
          <Button
            onClick={sendAndApproveInvite}
            disabled={sending}
            variant="contained"
            size="medium"
            title="Create an already-approved invite (no email); user can set up their account immediately"
          >
            Issue + Approve
          </Button>
        </Box>
      </Paper>

      {/* Invitation list */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Invitations &amp; Devices
      </Typography>
      {invitationRows.map((inv) => {
        const expired = isExpired(inv);
        const activeStep = getActiveStep(inv.status);
        const isDevice = inv.type === 'device';
        const remaining =
          inv.status === 'approved' ? timeRemaining(inv.expires_at) : null;

        return (
          <Paper key={inv.id} sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {inv.email}
                  </Typography>
                  <Chip
                    label={isDevice ? 'Device' : 'Invite'}
                    size="small"
                    variant="outlined"
                    color={isDevice ? 'primary' : 'default'}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Invited by {inv.invited_by} on{' '}
                  {new Date(inv.created_at).toLocaleDateString()}
                </Typography>
                {isDevice && inv.source_ip && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    IP: {inv.source_ip}
                    {inv.geo_location ? ` — ${inv.geo_location}` : ''}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {inv.status === 'denied' && (
                  <Chip label="Denied" color="error" size="small" />
                )}
                {inv.status === 'registered' && (
                  <Chip label="Registered" color="success" size="small" />
                )}
                {remaining && !remaining.expired && remaining.label && (
                  <Chip
                    label={`Approved — ${remaining.label}`}
                    color="info"
                    size="small"
                  />
                )}
                {remaining && remaining.expired && (
                  <Chip label="Approval expired" color="warning" size="small" />
                )}
                {expired && (
                  <Chip label="Expired" color="warning" size="small" />
                )}
              </Box>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
              {STEPS.map((label, index) => (
                <Step
                  key={label}
                  completed={
                    inv.status === 'denied' ? index < 1 : index < activeStep
                  }
                >
                  <StepLabel
                    StepIconComponent={(iconProps) => (
                      <CustomStepIcon
                        {...iconProps}
                        status={inv.status}
                        stepIndex={index}
                      />
                    )}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {inv.status === 'pending' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={actionLoading === inv.id}
                    onClick={() => approveInvite(inv)}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={actionLoading === inv.id}
                    onClick={() => denyInvite(inv)}
                  >
                    Deny
                  </Button>
                </>
              )}
              {inv.status === 'approved' && expired && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionLoading === inv.id}
                  onClick={() => resendInvite(inv)}
                >
                  Resend
                </Button>
              )}
              {inv.status === 'registered' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={actionLoading === inv.id}
                  onClick={() => revokeInvite(inv)}
                >
                  Revoke
                </Button>
              )}
              {inv.status === 'denied' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionLoading === inv.id}
                  onClick={() => reapproveInvite(inv)}
                >
                  Re-approve
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={actionLoading === inv.id}
                onClick={() => setDeviceApproval({ email: inv.email, ip: '' })}
                title="Manually approve a device by typing its IP address"
              >
                Approve Device by IP
              </Button>
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={actionLoading === inv.id}
                onClick={() => deleteInvite(inv)}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        );
      })}

      {invitationRows.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
          No invitations yet.
        </Typography>
      )}

      {/* Manual device-approval: prompt for the IP the registering device will use */}
      <Dialog
        open={!!deviceApproval}
        onClose={() => setDeviceApproval(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Approve a Device by IP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use this only if there&apos;s no pending request to approve above.
            Manually approve device registration for{' '}
            <strong>{deviceApproval?.email}</strong>. Enter the public IP
            address of the device that will register — it must match the IP that
            device uses. Valid for {DEVICE_APPROVAL_MINUTES} minutes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="IP Address"
              value={deviceApproval?.ip ?? ''}
              onChange={(e) =>
                setDeviceApproval((prev) =>
                  prev ? { ...prev, ip: e.target.value } : prev,
                )
              }
              placeholder="e.g. 172.59.225.164"
              fullWidth
              autoFocus
              size="small"
            />
            <Button
              onClick={autoDetectIp}
              size="small"
              sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
              title="Fill in the public IP of the device you're using right now"
            >
              Use my IP
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceApproval(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={approvingDevice || !deviceApproval?.ip.trim()}
            onClick={() => {
              if (deviceApproval) {
                approveDeviceForIp(deviceApproval.email, deviceApproval.ip);
              }
            }}
          >
            {approvingDevice ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// --- Users Tab ---

function UsersTab() {
  const [users, setUsers] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const fetchCredentials = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabaseData
      .from('webauthn_credentials')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load credentials',
        severity: 'error',
      });
      setLoading(false);
      return;
    }

    // Group by username
    const grouped: Record<string, Credential[]> = {};
    (data || []).forEach((cred: Credential) => {
      if (!grouped[cred.username]) grouped[cred.username] = [];
      grouped[cred.username].push(cred);
    });

    const userGroups: UserGroup[] = Object.entries(grouped).map(
      ([username, credentials]) => {
        const lastLogin = credentials.reduce<string | null>((latest, cred) => {
          if (!cred.last_used_at) return latest;
          if (!latest) return cred.last_used_at;
          return new Date(cred.last_used_at) > new Date(latest)
            ? cred.last_used_at
            : latest;
        }, null);
        return { username, credentials, lastLogin };
      },
    );

    setUsers(userGroups);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const [revokeConfirm, setRevokeConfirm] = useState<{
    credentialId: string;
    username: string;
  } | null>(null);
  const [resetConfirm, setResetConfirm] = useState<{
    username: string;
    count: number;
  } | null>(null);

  // Revoke ALL of a user's credentials, giving them a clean slate. After this,
  // the user re-adds a device (which the admin approves) to set up again. Note:
  // an admin can't create a passkey for the user — this just clears the blocker.
  const resetUserAccess = async (username: string) => {
    setResetConfirm(null);
    const { error } = await supabaseData
      .from('webauthn_credentials')
      .delete()
      .eq('username', username);

    if (error) {
      setSnackbar({
        open: true,
        message: `Failed to reset user: ${error.message}`,
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: `Reset ${username}. Have them open the app and choose "Add this device", then approve the request here.`,
        severity: 'success',
      });
      fetchCredentials();
    }
  };

  const revokeCredential = async (credentialId: string, username: string) => {
    setRevokeConfirm(null);
    const { error } = await supabaseData
      .from('webauthn_credentials')
      .delete()
      .eq('credential_id', credentialId)
      .eq('username', username);

    if (error) {
      setSnackbar({
        open: true,
        message: 'Failed to revoke credential',
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Credential revoked',
        severity: 'success',
      });
      fetchCredentials();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={fetchCredentials} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {users.map((userGroup) => (
        <Paper key={userGroup.username} sx={{ mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() =>
              setExpanded(
                expanded === userGroup.username ? null : userGroup.username,
              )
            }
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {userGroup.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userGroup.credentials.length} credential
                {userGroup.credentials.length !== 1 ? 's' : ''} • Last login:{' '}
                {userGroup.lastLogin
                  ? new Date(userGroup.lastLogin).toLocaleString()
                  : 'Never'}
              </Typography>
            </Box>
            {expanded === userGroup.username ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </Box>

          <Collapse in={expanded === userGroup.username}>
            <Box sx={{ px: 2, pb: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Device / Host</TableCell>
                      <TableCell>Authenticator</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell>Counter</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userGroup.credentials.map((cred) => (
                      <TableRow key={cred.credential_id}>
                        <TableCell>
                          {cred.friendly_name || 'Unnamed credential'}
                        </TableCell>
                        <TableCell>{lookupAaguid(cred.aaguid)}</TableCell>
                        <TableCell>
                          {cred.backed_up ? 'Synced ☁️' : 'Device-bound 📱'}
                        </TableCell>
                        <TableCell>
                          {new Date(cred.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {cred.last_used_at
                            ? new Date(cred.last_used_at).toLocaleString()
                            : 'Never'}
                        </TableCell>
                        <TableCell>{cred.counter}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            title="Revoke credential"
                            onClick={() =>
                              setRevokeConfirm({
                                credentialId: cred.credential_id,
                                username: userGroup.username,
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() =>
                    setResetConfirm({
                      username: userGroup.username,
                      count: userGroup.credentials.length,
                    })
                  }
                >
                  Reset user access
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>
      ))}

      {users.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
          No registered users.
        </Typography>
      )}

      <Dialog open={!!resetConfirm} onClose={() => setResetConfirm(null)}>
        <DialogTitle>Reset User Access</DialogTitle>
        <DialogContent>
          <Typography>
            This revokes <strong>all {resetConfirm?.count}</strong> passkey(s)
            for <strong>{resetConfirm?.username}</strong>, giving them a clean
            slate. They&apos;ll need to open the app, choose &ldquo;Add this
            device&rdquo;, and have you approve the request to set up again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (resetConfirm) resetUserAccess(resetConfirm.username);
            }}
          >
            Reset access
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revokeConfirm} onClose={() => setRevokeConfirm(null)}>
        <DialogTitle>Revoke Credential</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke this credential for{' '}
            <strong>{revokeConfirm?.username}</strong>? They won&apos;t be able
            to log in with this passkey anymore.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (revokeConfirm) {
                revokeCredential(
                  revokeConfirm.credentialId,
                  revokeConfirm.username,
                );
              }
            }}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// --- Admin Panel (Main) ---

const AdminPanel: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1100,
        mx: 'auto',
        p: 2,
        overflowY: 'auto',
        maxHeight: '100%',
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Panel
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Invitations" />
        <Tab label="Users" />
      </Tabs>

      {tab === 0 && <InvitationsTab />}
      {tab === 1 && <UsersTab />}
    </Box>
  );
};

export default AdminPanel;
